import { ENEMY_IDS } from '../model/enemies'
import { TOWER_CATALOG } from '../model/towers'
import {
  ENEMY_VISUALS,
  FALLBACK_ENEMY_VISUAL
} from './enemies'
import {
  FALLBACK_PROJECTILE_VISUAL,
  PROJECTILE_VISUALS
} from './projectiles'
import {
  FALLBACK_TOWER_VISUAL,
  TERRAIN_VISUALS,
  TOWER_VISUALS
} from './towers'
import type {
  EnemyVisualDefinition,
  ProjectileVisualDefinition,
  TerrainVisualDefinition,
  TowerVisualDefinition
} from './types'

const TOWER_IDS = Object.keys(TOWER_CATALOG)

const warned = new Set<string>()

function warnMissing(kind: string, id: string): void {
  const key = `${kind}:${id}`
  if (warned.has(key)) return
  warned.add(key)
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[shapes-td visual] missing ${kind} definition "${id}" — using fallback`)
  }
}

export function resolveTowerVisual(definitionId: string): TowerVisualDefinition {
  const visual = TOWER_VISUALS[definitionId]
  if (visual) return visual
  warnMissing('tower', definitionId)
  return { ...FALLBACK_TOWER_VISUAL, definitionId }
}

export function resolveEnemyVisual(definitionId: string): EnemyVisualDefinition {
  const visual = ENEMY_VISUALS[definitionId]
  if (visual) return visual
  warnMissing('enemy', definitionId)
  return { ...FALLBACK_ENEMY_VISUAL, definitionId }
}

export function resolveProjectileVisual(definitionId: string): ProjectileVisualDefinition {
  const visual = PROJECTILE_VISUALS[definitionId]
  if (visual) return visual
  warnMissing('projectile', definitionId)
  return { ...FALLBACK_PROJECTILE_VISUAL, definitionId }
}

export function resolveTerrainVisual(id: TerrainVisualDefinition['id']): TerrainVisualDefinition {
  return TERRAIN_VISUALS[id]
}

/** Coverage check used by tests — every gameplay id must have a visual. */
export function visualCoverage(): {
  towersOk: boolean
  enemiesOk: boolean
  missingTowers: string[]
  missingEnemies: string[]
} {
  const missingTowers = TOWER_IDS.filter((id) => !TOWER_VISUALS[id])
  const missingEnemies = ENEMY_IDS.filter((id) => !ENEMY_VISUALS[id])
  return {
    towersOk: missingTowers.length === 0,
    enemiesOk: missingEnemies.length === 0,
    missingTowers,
    missingEnemies
  }
}
